import React, { useEffect, useRef } from 'lib/react';
import { Router } from 'lib/router';
import { useDispatch, useSelector } from 'lib/redux';
import {
  GET_USER_ENTITLEMENTS, GET_INIT_PARAMETERS, LOAD_USER_PRODUCTS,
  GET_USER_ENTITLED_APPS,
  GET_ENTITLED_THIRDPARTY_APPS, // #genai
  GET_USER_PROFILE,
} from 'constants/ActionTypes';
import CancelOrderDialog from 'features/cancel-order-dialog';
import NotificationBar from 'features/notificationBar';
import { makeStyles } from 'lib/components';
import { AUTHORIZE_ROUTE } from 'constants/RouteConstants';
import Header from 'commonComponents/header';
import SkipNavigation from 'features/skipNavigation';
import { isAuthorized } from 'utils/auth.utils';
import { gainsightPxIdentifyUser } from 'utils/gainsightPx.utils';
import { isOpenSitemapPath } from 'utils/sitemapOpenPage.utils';
import Footer from './commonComponents/footer';
import Routes from './routes';
import history from './store/CreateHistory';

const useStyles = makeStyles((theme) => ({
  app: {
    height: '100vh',
    display: 'flex',
    flexFlow: 'column',
    overflowY: 'scroll',
    backgroundColor: theme.evPalette.charcoal[100],
    '@media print': {
      height: 'fit-content !important',
    },
  },
}));

function App() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const bootstrappedRef = useRef(false);

  const userProfile = useSelector(({ userData }) => userData.userProfile);

  useEffect(() => {
    const maybeBootstrap = (nextPathname) => {
      // Anonymous open-email visitors: skip bootstrap (no token / no subscription APIs).
      // Logged-in users (e.g. Order History → sitemap new tab) still need entitlements,
      // init-parameters, and products so subscription Usage can load. Stale-token 401s
      // on these paths do not redirect (see fetchInterceptor).
      if (isOpenSitemapPath(nextPathname) && !isAuthorized()) return;
      if (bootstrappedRef.current) return;
      if (isAuthorized() && nextPathname !== AUTHORIZE_ROUTE) {
        bootstrappedRef.current = true;
        dispatch({ type: GET_INIT_PARAMETERS });
        dispatch({ type: LOAD_USER_PRODUCTS });
        dispatch({ type: GET_USER_ENTITLEMENTS });
        dispatch({ type: GET_USER_ENTITLED_APPS });
        dispatch({ type: GET_ENTITLED_THIRDPARTY_APPS }); // #genai
        dispatch({ type: GET_USER_PROFILE });
      }
    };

    maybeBootstrap(history.location.pathname);
    const unlisten = history.listen(({ pathname: nextPathname }) => {
      maybeBootstrap(nextPathname);
    });
    return unlisten;
  }, [dispatch]);

  useEffect(() => {
    if (userProfile) {
      gainsightPxIdentifyUser(userProfile);
    }
  }, [userProfile]);

  const entitlements = useSelector(({ userEntitlements }) => userEntitlements);

  // Authorized users (including OH → sitemap new tab) wait for entitlements so
  // SUBSCRIPTION_DASHBOARD_V2 is known before subscription Usage renders.
  // Anonymous open-email visitors skip this wait (no token / no entitlements call).
  if (isAuthorized() && entitlements === null) return <>Loading...</>;

  return (
    <Router history={history}>
      <div className={classes.app}>
        <SkipNavigation />
        <Header />
        <Routes />
        <Footer />
        <CancelOrderDialog />
        <NotificationBar />
      </div>
    </Router>
  );
}

export default App;
